import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { TLearnerForm } from "@/features/learner/types/learnerForm";
import { PROCEDURE_STATUS } from "@/api/services/main/exam-steps/dtos/get-exam-step-learners.dto";
import { useDialog } from "@/hooks/useDialog";

type Args = {
  control: Control<TLearnerForm>;
  setValue: UseFormSetValue<TLearnerForm>;
  /** True on the new-learner create screen. Suppresses the confirm modal on OFF. */
  isCreate: boolean;
  /** Persisted 手続ステータス (read-only). Used solely to decide whether to show the confirm modal. */
  procedureStatusKey: string | null;
};

/**
 * Owns the 手続き管理を利用する toggle UX.
 *
 * The actual `procedureStatusKey` mutation is performed server-side — by the learner
 * create/update endpoints AND by the exam-steps update-status endpoint. The FE only
 * sends the toggle value and reads the persisted status to decide when to confirm.
 *
 * - ON → OFF: on edit, if current status is neither NEW_REGISTRATION nor OUT_OF_SCOPE,
 *   prompt the user with a confirm modal first. Otherwise apply directly.
 * - OFF → ON: applied directly (BE resets the status to NEW_REGISTRATION).
 */
export const useProcedureManagementToggle = ({ control, setValue, isCreate, procedureStatusKey }: Args) => {
  const toggle = useWatch({ control, name: "isProcedureManaged" });
  const confirmDialog = useDialog();

  const applyToggle = (next: boolean) => {
    setValue("isProcedureManaged", next, { shouldDirty: true, shouldValidate: true });
  };

  const requestToggle = async (next: boolean) => {
    if (next === false) {
      const needsConfirm =
        !isCreate &&
        !!procedureStatusKey &&
        procedureStatusKey !== PROCEDURE_STATUS.NEW_REGISTRATION &&
        procedureStatusKey !== PROCEDURE_STATUS.OUT_OF_SCOPE;
      if (needsConfirm) {
        const ok = await confirmDialog.show();
        if (!ok) return;
      }
    }
    applyToggle(next);
  };

  return {
    isProcedureManaged: toggle,
    requestToggle,
    isConfirmOpen: confirmDialog.open,
    closeConfirm: confirmDialog.cancel,
    confirmOff: confirmDialog.confirm,
  };
};
